import PartnerLogoSprite from "./PartnerLogoSprite";
import { PARTNERS } from "../data/partners";

const LOGO_VIEW_BOX = "0 0 652 652";

// Fills its shape via a raster <pattern><image> data URI rather than plain
// vector paths, which doesn't reliably render nested inside a sprite
// <symbol> across browsers — served as a plain <img> instead.
const SPRITE_EXCLUDED = new Set(["carlo-migliavacca"]);

function maskStyle(file: string) {
  return {
    WebkitMaskImage: `url(/assets/brand/partners/${file}.svg)`,
    maskImage: `url(/assets/brand/partners/${file}.svg)`,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    maskSize: "contain",
  } as const;
}

// Lives inside Hero on desktop (rendered as a band pinned to its bottom edge
// over the video) — transparent background, white/70% logos so they read
// against the dark video + overlay instead of a grey band. On mobile it's
// pulled out into its own section between Hero and the catalogue preview
// instead (see App.tsx), so `tone="dark"` swaps the mask fill to a muted
// ink color that reads against that section's grey background.
export default function PartnersMarquee({
  className = "",
  tone = "light",
}: {
  className?: string;
  tone?: "light" | "dark";
}) {
  return (
    <div className={`overflow-hidden ${className}`}>
      <PartnerLogoSprite />
      {/* The fade mask lives on this inner layer so only the sliding logos
          fade at the edges — there's no background box to protect here
          since the band itself is transparent. */}
      <div className="[-webkit-mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)] [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
        <div className="partner-marquee flex w-max items-center gap-12 sm:gap-16 lg:gap-28">
          {[...PARTNERS, ...PARTNERS].map(({ name, file }, i) => (
            <span
              key={`${file}-${i}`}
              // width is locked equal to height (not w-auto) — the source
              // viewBox is a perfect square (652x652), so this renders
              // identically to the old content-derived width, but without
              // waiting on the sprite's <symbol> or the excluded SVG file
              // to load before the box knows its own size.
              className="relative inline-block h-28 w-28 shrink-0 sm:h-[104px] sm:w-[104px] lg:h-[125px] lg:w-[125px]"
            >
              {/* Always rendered in normal flow so this box has a real width to
                  size against — invisible, the white mask below covers it. */}
              {SPRITE_EXCLUDED.has(file) ? (
                <img
                  src={`/assets/brand/partners/${file}.svg`}
                  alt={i < PARTNERS.length ? name : ""}
                  aria-hidden={i >= PARTNERS.length}
                  title={name}
                  decoding="async"
                  draggable={false}
                  className="h-full w-full object-contain opacity-0"
                />
              ) : (
                <svg
                  viewBox={LOGO_VIEW_BOX}
                  role="img"
                  aria-hidden={i >= PARTNERS.length}
                  className="h-full w-full opacity-0"
                >
                  {i < PARTNERS.length && <title>{name}</title>}
                  <use href={`#logo-${file}`} />
                </svg>
              )}
              {/* Silhouette on top of the (invisible) image above — white at
                  70% opacity against Hero's dark video, or muted ink against
                  the light section it sits in on mobile. */}
              <span
                aria-hidden="true"
                className={
                  tone === "dark" ? "absolute inset-0 bg-ink opacity-40" : "absolute inset-0 bg-white opacity-70"
                }
                style={maskStyle(file)}
              />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
